import {
	Auditable,
	CustomException,
	DB_Object,
	Timestamp
} from "@intuitionrobotics/ts-common";
import {DeviceType, Unit} from "@app/ir-q-app-common/types/units";

export const SharedArtifactsKey = 'shared';
// Not a device: the packages a 3.0 (single-device) unit does not get from the SOM list (listProductsForUi).
export const Exclude3point0Key = 'exclude3point0';

// APK upload (three calls, no bucket listener): upload-url → the browser PUTs the file → register.
// The signed URL is bound to this content type, so the PUT must send exactly it.
export const ApkMimeType = "application/vnd.android.package-archive";
export type ApkUpload = { uploadId: string, name: string };
export type ApkUploadSlot = ApkUpload & { secureUrl: string };
export type Request_ApkUploadUrls = { names: string[] };
export type Request_RegisterApks = { uploads: ApkUpload[] };

export class ApkParseException
	extends CustomException {
	constructor(message: string, cause?: Error) {
		super(ApkParseException, message, cause);
	}
}

export type Artifact = {
	packageName: string;
	versionName: string;
}

export type UiMeta = MetaData & {
	key: string
}

export type FullMetaData = {
	metaData: MetaData
	icon?: string
}

export type MetaData = Artifact & {
	rawVersionName: string
	name: string;
	versionCode: number;
	type: string;
	fileMd5: string;
	signatureMd5: string;
	// When the artifact was registered (its audit time); returned by artifact/list.
	uploadedAt?: number;
}

export type Storage_ArtifactMetaData = DB_Object & MetaData & Auditable & {
	// A path in the ONE PM artifacts bucket: server-api's com.ir.service.storage.pm.bucket (read from GET
	// /pm/admin/state). No per-artifact bucket; a legacy Firestore doc's bucketName is ignored.
	filePath: string;
	uploadId?: string;
}

export type Response_ArtifactMetaData = MetaData & {
	downloadUrl: string
	iconUrl?: string
	expireTime: number
}

type configIdObject = {
	configId: string
}

export type Request_removeConfig = configIdObject

export type Request_ListUnitsByConfig = configIdObject

export type SecureUrl = {
	fileName: string
	securedUrl: string
	publicUrl: string
}

export type ArtifactUrls = {
	artifactUrl: SecureUrl | undefined
	iconUrl: SecureUrl | undefined
}

export type Response_GetStatus = PmStatus[]
// /v1/status/query body: unitId narrows to one unit (server-api's packages filter is deliberately not used — the
// Units tab counts across the whole artifact_data).
export type Request_QueryStatus = Partial<PmStatus>

export type AppInfo = DB_Object & {
	name: string
	packageName: string
	icon: string
	latestVersion: string
}

export type Response_AppInfo = AppInfo & {
	iconUrl: string
}

export type Response_Aliases = {}

export type Response_UnitInfo = UnitInfo[]

export type UnitInfo = Unit & {
	aliasId?: string
	configId?: string
}

export type Request_ListAppVersions = {
	packageName: string
}

export type ProductAndDevices = {
	[key: string]: Devices
};

export type Devices = {
	shared: string[]
	[k: string]: string[]
};

export type DeviceConfig = {
	deviceType: string;
	artifacts: Artifact[];
}

export type BasePackageConfig = ReleaseCandidate & {
	name: string;
	description?: string;
	configId: string;
	product: string;
}

export type ReleaseCandidate = {
	releaseCandidate?: boolean,
	releaseCandidateFromVersion?: string
	releaseCandidateToVersion?: string
	updateToLatestFirmware?: boolean
}

export type PackageConfig = DB_Object & BasePackageConfig & Auditable & {
	deviceConfig: DeviceConfig[];
}

export type PackageConfigFe = {
	configName: string
	deviceConfig: FeDeviceConfigList
	aliasName?: string
	product: string
	unitId: string
}

export type FeDeviceConfigList = {
	artifactList: Artifact[]
	deviceType: string
}[];

export enum InstallationState {
	RemoteInstallBlocked = "RemoteInstallBlocked",
	NewVersionAvailable = "NewVersionAvailable",
	NotDownloaded = "NotDownloaded",
	Downloading = "Downloading",
	// on-device delta download — the device (Strickland ApkStates) emits this and
	// the four below; they used to be missing here and rendered as a neutral
	// "unknown" status in the support tool. Kept in sync with
	// ir-strickland .../pm/model/ApkStates.java.
	DownloadingDelta = "DownloadingDelta",
	Downloaded = "Downloaded",
	PatchingUpdate = "PatchingUpdate",
	Patched = "Patched",
	InstalledDifferentVersion = "InstalledDifferentVersion" ,
	Uninstalling = "Uninstalling",
	Uninstalled = "Uninstalled",
	UninstallBlocked = "UninstallBlocked",
	Installing = "Installing",
	VerifyingVersion = "VerifyingVersion",
	InstalledUpToDate = "InstalledUpToDate",
	WaitingReconnection = "WaitingReconnection",
	Canceled = "Canceled",
	ERROR = "ERROR"

}

/**
 * Semantic bucket for an installation state, used to colour the status in the UI.
 * Every raw {@link InstallationState} maps to exactly one bucket.
 */
export enum InstallStateBucket {
	UpToDate = "UpToDate",     // installed & current            (green)
	InProgress = "InProgress", // actively downloading/installing (blue)
	Staged = "Staged",         // ready/waiting for the next step (amber)
	Mismatch = "Mismatch",     // installed, wrong version        (orange)
	Error = "Error",           // broken / never downloaded       (red)
	Inert = "Inert"            // blocked / cancelled / removed   (grey)
}

const INSTALL_STATE_BUCKETS: { [lowerCaseState: string]: InstallStateBucket } = {
	installeduptodate: InstallStateBucket.UpToDate,

	downloading: InstallStateBucket.InProgress,
	downloadingdelta: InstallStateBucket.InProgress,
	patchingupdate: InstallStateBucket.InProgress,
	installing: InstallStateBucket.InProgress,
	uninstalling: InstallStateBucket.InProgress,
	verifyingversion: InstallStateBucket.InProgress,

	newversionavailable: InstallStateBucket.Staged,
	downloaded: InstallStateBucket.Staged,
	patched: InstallStateBucket.Staged,
	waitingreconnection: InstallStateBucket.Staged,
	// "download not yet initiated" — the device colours this distinctly from ERROR
	// (a pale pink, not red), so it's a pending/staged state, not a failure.
	notdownloaded: InstallStateBucket.Staged,

	installeddifferentversion: InstallStateBucket.Mismatch,

	// ERROR is the only red the device emits (apk_state__error = #FF0000).
	error: InstallStateBucket.Error,

	remoteinstallblocked: InstallStateBucket.Inert,
	uninstalled: InstallStateBucket.Inert,
	uninstallblocked: InstallStateBucket.Inert,
	canceled: InstallStateBucket.Inert
};

/**
 * The base state name with any download-progress suffix stripped.
 *
 * The device rewrites a live download's state as "<Name> <N>%" — e.g.
 * "Downloading 42%" / "DownloadingDelta 7%" (see
 * ir-strickland .../pm/InstallationWorker.java, which does
 * `state.split(" ")[0] + " " + percentage + "%"`). Only Downloading /
 * DownloadingDelta carry a percentage, but we strip any trailing " <N>%" to be safe.
 */
export const baseInstallState = (state?: string): string =>
	(state || "").trim().replace(/\s+\d+\s*%$/, "").trim();

/**
 * Map a raw installation-state string (percentage suffix and case-insensitive) to
 * its semantic {@link InstallStateBucket}. Unknown states fall back to `Inert` so
 * a never-before-seen state reads as neutral rather than mis-coloured.
 */
export const installStateBucket = (state?: string): InstallStateBucket =>
	INSTALL_STATE_BUCKETS[baseInstallState(state).toLowerCase()] ?? InstallStateBucket.Inert;

export type AndroidArtifactData = {
	appName: string
	errorsAsString: string
	installationState: InstallationState
	installationTime: number
	packageName: string
	// not stored by server-api: absent in what Support reads
	sharedUserId?: string
	signatures?: string[]
	// the device reports version codes as strings; server-api returns them as sent (absent when not numeric)
	versionCode?: string
	versionName: string
	newVersionCode: string
	newVersionName: string
	installedVia?: string
};

export type AndroidConfigMetadata = {
	device_type: string
	name: string
	product: string
	unit_id: string
};

export type AndroidStatus = {
	artifact_data?: AndroidArtifactData[]
	config_metadata?: AndroidConfigMetadata
};

export type Base_PmStatus = {
	sha256: string
	status: AndroidStatus
}

export type PmStatus = DB_Object & Base_PmStatus & {
	unitId: string
	product: string
	deviceType: string
	deviceId?: string
	// empty for a bare server-api row (a device that has not reported yet)
	timestamp: Partial<Timestamp>
	isRC?: boolean
	startTime?: number
	endTime?: number
}

export type Request_Configs = {
	product: string
}

export type Request_Config = {
	configId: string
}

export type UnitWith3point0Indication = Unit & { is3point0Unit?: boolean }

export type Request_ConfigListFe = {
	units: UnitWith3point0Indication[]
}

export type Request_CreateAliases = {
	alias: BaseAlias
	units: Unit[]
}

export type Request_DeleteAlias = {
	aliasId: string
}

export type Request_ResetUnits = {
	unitIds: string // comma-separated unit ids
}

export type BodyUnitInstall = {
	units: Unit[]
	deviceType?: DeviceType
}

export type PatchMetaData = DB_Object & {
	filePath: string
	bucketName: string
	packageName: string
	toVersionName: string
	fromVersionName: string
}

export type Request_AddManyPatches = {
	patches: Omit<PatchMetaData, "_id">[]
}

// Jenkins CI: create/update a config by server-side parsing APKs at bucket paths.
export type Body_UpsertConfigByApkPaths = {
	productKey: string
	apkPaths: string[]
	// Ignored (still accepted from older Jenkins jobs): the APKs are read from the PM artifacts bucket.
	bucketName?: string
	configName: string
}

export type ApkMetadata = {
	packageName: string
	versionName: string
	name: string
	versionCode: string
	filePath: string
	fileMd5: string
	buildType: string
}

// Jenkins CI: create/update a config from already-parsed APK metadata (no
// server-side APK parsing), optionally assign units + write env overrides.
export type Body_UpsertConfigByApkMetadata = {
	apks?: ApkMetadata[] | null
	// Ignored (still accepted from older Jenkins jobs): the APKs are read from the PM artifacts bucket.
	bucketName?: string
	configName: string
	apiServerUrl?: string
	speechServerUrl?: string
	uploadDataLabel?: string
	unitIds?: string
	blame?: string
	versionApp: string
}

export type Request_ListArtifacts = {
	ids?: string[]
}

export type Response_InstallCompleted = {
	[product: string]: {
		[unitId: string]: {
			[deviceName: string]: boolean
		}
	}
}

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };
type XOR<T, U> = (T | U) extends object ? (Without<T, U> & U) | (Without<U, T> & T) : T | U;

export type Request_AssignTo = {
	unit: Unit
} & XOR<{
	configId: string
}, {
	aliasId: string
}>

export type BaseRequest_Alias = {
	configId: string
	name: string
	description: string
	// Kaspero content-version label (CONST_AssignedContentVersionLabelKey) pushed
	// to every unit assigned to the alias.
	versionContentLinkLabel?: string
}

export type Request_Alias = BaseRequest_Alias & {
	rollout: { updateTime: number }
}

export type BaseAlias = Request_Alias & {
	id?: string
}

export type Alias = DB_Object & Request_Alias & Auditable & {
	id: string
}

export type UnitConfig = DB_Object & Unit & Auditable & {
	updateAt: number;
	configId: string;
}

export type AliasId = string
